// components/models/QwenEditMultiangleControlPanel.tsx
import React, { memo, useCallback } from 'react';
import RangeControl from '../ui/RangeControl';
import { QwenEditMultiangleParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface QwenEditMultiangleControlPanelProps {
  params: QwenEditMultiangleParams;
  setModelParams: (
    updater: (
      prevParams: { [model: string]: QwenEditMultiangleParams }
    ) => { [model: string]: QwenEditMultiangleParams }
  ) => void;
  diffusionModel: string;
}

export const getDefaultQwenEditMultiangleParams = (): QwenEditMultiangleParams => ({
  prompt: '',
  seed: -1,
  lora_scale: 1.2,
  guidance_scale: 4.5,
  zoom: 5,
  horizontal_angle: 0,
  vertical_angle: 0,
  num_inference_steps: 40,
  enable_safety_checker: true,
  sync_mode: true,
});

const QwenEditMultiangleControlPanel: React.FC<QwenEditMultiangleControlPanelProps> = ({
  params,
  setModelParams,
  diffusionModel,
}) => {
  const { kernel } = useKernel();

  const applyParamUpdate = useCallback((
    name: keyof QwenEditMultiangleParams,
    value: QwenEditMultiangleParams[keyof QwenEditMultiangleParams],
  ) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[QwenEditMultiangleControlPanel] Missing params for model "${diffusionModel}".`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value as any,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'QwenEditMultiangleControlPanel',
    });
  }, [diffusionModel, setModelParams, kernel]);

  const handleNumericParamChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const parsedValue = parseFloat(String(value));
    applyParamUpdate(name as keyof QwenEditMultiangleParams, parsedValue as QwenEditMultiangleParams[keyof QwenEditMultiangleParams]);
  }, [applyParamUpdate]);

  return (
    <>
      <div className="mb-4">
        <RangeControl
          label="LoRA Scale"
          name="lora_scale"
          min={0}
          max={4}
          step={0.05}
          value={params.lora_scale}
          onChange={handleNumericParamChange}
          description="Controls the strength of the camera control LoRA."
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Guidance Scale"
          name="guidance_scale"
          min={0}
          max={10}
          step={0.1}
          value={params.guidance_scale}
          onChange={handleNumericParamChange}
          description="Classifier-free guidance intensity. Higher = more literal instructions."
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Zoom"
          name="zoom"
          min={0}
          max={10}
          step={0.1}
          value={params.zoom}
          onChange={handleNumericParamChange}
          description="Camera zoom level. 0 = wide, 10 = extreme close-up."
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Horizontal Angle"
          name="horizontal_angle"
          min={0}
          max={360}
          step={1}
          value={params.horizontal_angle}
          onChange={handleNumericParamChange}
          description="Rotate around the subject left/right (0deg to 360deg)."
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Vertical Angle"
          name="vertical_angle"
          min={-30}
          max={90}
          step={1}
          value={params.vertical_angle}
          onChange={handleNumericParamChange}
          description="Tilt the camera up/down in degrees (-30deg low angle to 90deg bird's-eye)."
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Num Inference Steps"
          name="num_inference_steps"
          min={1}
          max={50}
          step={1}
          value={params.num_inference_steps}
          onChange={handleNumericParamChange}
          description="Controls detail/consistency. Higher values take longer (1-50)."
        />
      </div>

    </>
  );
};

export default memo(QwenEditMultiangleControlPanel);

